import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Building2 } from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { inr } from "@/lib/api";
import { POC_ROLES } from "@/constants/crm";
import { PageHeader, Panel, StatusBadge, PillTabs } from "@/components/crm/common";
import { ProjectSelect } from "@/components/crm/ProjectSelect";
import { ExpenseTable, FinancialSummary } from "@/components/crm/Expenses";
import { PocPanel } from "@/components/crm/PocPanel";

const TABS = [["overview", "Overview"], ["vendor", "Vendor Expenses"], ["internal", "Internal Expenses"], ["poc", "POC & Team"]];

function OverviewTab({ p, team }) {
  const q = { project_id: p.id };
  const { items: vendor } = useCollection("vendor_expenses", q);
  const { items: internal } = useCollection("internal_expenses", q);
  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
      <FinancialSummary project={p} vendor={vendor} internal={internal} />
      <Panel testid="overview-team-panel" title="Project team" action={<Link to={`/poc?project=${p.id}`} className="text-xs font-semibold text-brand">Manage</Link>}>
        <div className="space-y-3">
          {POC_ROLES.map(([k, label]) => {
            const m = team.find((t) => t.id === p.poc?.[k]);
            return (
              <div key={k} className="flex items-center gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-50 text-xs font-bold text-violet-700">{m ? m.name[0] : "?"}</span>
                <div className="min-w-0"><div className="text-[11px] text-zinc-400">{label}</div><div className="truncate text-sm font-medium text-zinc-800">{m?.name || <span className="text-zinc-400">Unassigned</span>}</div></div>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}

export default function ProjectOverview() {
  const { items: projects, update } = useCollection("projects");
  const { items: team } = useCollection("team");
  const [pid, setPid] = useState(new URLSearchParams(window.location.search).get("project") || "");
  const [tab, setTab] = useState("overview");
  const p = projects.find((x) => x.id === pid);

  return (
    <>
      <PageHeader num="13" title="Project Overview" subtitle="Each project is the central record — budget, status, team, expenses and POC.">
        <ProjectSelect projects={projects} value={pid} onChange={setPid} />
      </PageHeader>
      {p && (
        <>
          <Panel testid="project-header-card">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand text-lg font-bold text-white">{p.name[0]}</span>
                <div>
                  <div className="text-lg font-bold text-zinc-900">{p.name}</div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                    <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{p.client}</span>
                    <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{p.event_date || "Date TBC"}</span>
                    <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.venue || "Venue TBC"}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right"><div className="text-[11px] text-zinc-400">Budget</div><div className="text-lg font-bold text-zinc-900">{inr(p.budget)}</div></div>
                <div className="flex flex-col items-end gap-1"><StatusBadge value={p.stage} /><StatusBadge value={p.status} /></div>
              </div>
            </div>
          </Panel>
          <PillTabs testid="overview-tab" value={tab} onChange={setTab} tabs={TABS} />
          {tab === "overview" && <OverviewTab p={p} team={team} />}
          {tab === "vendor" && <ExpenseTable kind="vendor" projectId={p.id} projects={projects} />}
          {tab === "internal" && <ExpenseTable kind="internal" projectId={p.id} projects={projects} />}
          {tab === "poc" && <PocPanel project={p} team={team} update={update} />}
        </>
      )}
    </>
  );
}
